import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Spin, 
  Tag, 
  Button, 
  Row, 
  Col, 
  Descriptions, 
  Table, 
  Space, 
  message, 
  Breadcrumb,
  Image,
  Divider,
  Select,
  Form,
  Modal,
  Input,
  DatePicker
} from 'antd';
import { 
  ArrowLeftOutlined, 
  HomeOutlined, 
  ShoppingOutlined,
  LoadingOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CarOutlined,
  UserOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons';
import { ordersApi, deliveryPersonsApi } from '../../utils/api';
import moment from 'moment';

const { Title, Text } = Typography;

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // États pour la modification
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [loadingDeliveryPersons, setLoadingDeliveryPersons] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // États pour l'attribution de livreur
  const [isAssignmentModalVisible, setIsAssignmentModalVisible] = useState(false);
  const [assignmentForm] = Form.useForm();
  const [deliveryPersonsStats, setDeliveryPersonsStats] = useState({});

  // États pour la navigation entre commandes
  const [ordersList, setOrdersList] = useState([]);
  const [currentOrderIndex, setCurrentOrderIndex] = useState(-1);
  const [loadingNavigation, setLoadingNavigation] = useState(false);

  // Charger les détails de la commande via l'API serveur
  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      
      // Utiliser l'API serveur pour récupérer les détails de la commande
      const response = await ordersApi.getById(id);
      console.log(response);
             if (response.data) {
         // L'API serveur retourne directement les données avec les order_items inclus
         setOrder(response.data);
       } else {
         throw new Error('Erreur lors du chargement de la commande');
       }
      
    } catch (error) {
      console.error('Error loading order details:', error);
      
      if (error.response?.status === 404) {
        message.error("Commande non trouvée");
      } else if (error.response?.status === 401) {
        message.error("Vous devez être connecté pour voir les détails de la commande");
      } else {
        message.error("Erreur lors du chargement des détails de la commande");
      }
      
      navigate('/app/orders');
    } finally {
      setLoading(false);
    }
    };

  // Charger les statistiques des livreurs
  const loadDeliveryPersonsStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const statsPromises = deliveryPersons.map(person => 
        deliveryPersonsApi.getDailyStats(person.id, today)
      );
      
      const statsResults = await Promise.all(statsPromises);
      
      const statsMap = {};
      statsResults.forEach((result, index) => {
        if (result && result.data) {
          statsMap[deliveryPersons[index].id] = result.data;
        } else {
          statsMap[deliveryPersons[index].id] = { orders_count: 0 };
        }
      });
      
      setDeliveryPersonsStats(statsMap);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  // Charger la liste des livreurs
  const loadDeliveryPersons = async () => {
    try {
      setLoadingDeliveryPersons(true);
      // Utiliser l'API existante pour récupérer les livreurs actifs
      const response = await deliveryPersonsApi.getAll({ is_active: true });
      
      if (response && response.data) {
        setDeliveryPersons(response.data || []);
        // Charger les statistiques après avoir récupéré les livreurs
        await loadDeliveryPersonsStats();
      } else {
        throw new Error('Erreur lors du chargement des livreurs');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des livreurs:', error);
      message.error('Erreur lors du chargement des livreurs');
    } finally {
      setLoadingDeliveryPersons(false);
    }
  };

  // Charger la liste des commandes pour la navigation
  const loadOrdersList = async () => {
    try {
      setLoadingNavigation(true);
      
      // Récupérer toutes les commandes avec les mêmes filtres que la liste principale
      const response = await ordersApi.getAll({
        order_by: 'created_at',
        ascending: false,
        include_items: false,
        limit: 1000 // Récupérer un grand nombre pour avoir toute la liste
      });
      
      if (response && response.data) {
        const orders = response.data;
        setOrdersList(orders);
        
        // Trouver l'index de la commande actuelle
        const currentIndex = orders.findIndex(o => o.id === id);
        setCurrentOrderIndex(currentIndex);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la liste des commandes:', error);
    } finally {
      setLoadingNavigation(false);
    }
  };

  // Ouvrir le modal de modification
  const handleEditClick = () => {
    form.setFieldsValue({
      status: order.status,
      delivery_person_id: order.delivery_person_id,
      delivery_notes: order.delivery_notes
    });
    setIsEditModalVisible(true);
    loadDeliveryPersons();
  };

  // Sauvegarder les modifications
  const handleSave = async (values) => {
    try {
      setSaving(true);
      
      // Préparer les données à mettre à jour
      const updateData = {};
      
      // Vérifier les changements de statut
      if (values.status !== order.status) {
        updateData.status = values.status;
      }
      
      // Vérifier les changements de livreur
      if (values.delivery_person_id !== order.delivery_person_id) {
        updateData.delivery_person_id = values.delivery_person_id;
      }
      
      // Vérifier les changements de notes de livraison
      if (values.delivery_notes !== order.delivery_notes) {
        updateData.delivery_notes = values.delivery_notes;
      }
      
      // S'il n'y a pas de changements
      if (Object.keys(updateData).length === 0) {
        setIsEditModalVisible(false);
        message.info('Aucune modification détectée');
        return;
      }
      
      // Effectuer la mise à jour via l'API existante
      const response = await ordersApi.update(id, updateData);
      
      if (response && response.data) {
        message.success('Commande mise à jour avec succès');
        setIsEditModalVisible(false);
        // Recharger les détails de la commande
        await loadOrderDetails();
      } else {
        throw new Error('Erreur lors de la mise à jour');
      }
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error('Erreur lors de la mise à jour de la commande');
    } finally {
      setSaving(false);
    }
  };

  // Ouvrir le modal d'attribution de livreur
  const showAssignmentModal = () => {
    // Initialiser le formulaire avec les valeurs par défaut
    assignmentForm.setFieldsValue({
      delivery_person_id: order.delivery_person_id || null,
      delivery_date: order.delivery_date ? moment(order.delivery_date) : null,
      shipping_fee: order.shipping_fee || 0
    });
    
    setIsAssignmentModalVisible(true);
    if (deliveryPersons.length === 0) {
      loadDeliveryPersons();
    }
  };

  // Attribuer la commande à un livreur
  const handleAssignOrder = async () => {
    try {
      const values = await assignmentForm.validateFields();
      
      setSaving(true);
      
      // Utiliser l'API d'attribution des commandes
      const assignmentData = {
        delivery_person_id: values.delivery_person_id,
        delivery_date: values.delivery_date ? values.delivery_date.format('YYYY-MM-DD') : null,
        shipping_fee: values.shipping_fee || 0
      };

      await ordersApi.assignOrders([order.id], values.delivery_person_id, assignmentData);
      
      message.success('Commande attribuée avec succès');
      setIsAssignmentModalVisible(false);
      
      // Recharger les détails de la commande
      await loadOrderDetails();
      
    } catch (error) {
      console.error('Erreur lors de l\'attribution de la commande:', error);
      message.error('Erreur lors de l\'attribution de la commande');
    } finally {
      setSaving(false);
    }
  };

  // Navigation vers la commande précédente
  const goToPreviousOrder = () => {
    if (currentOrderIndex > 0) {
      const previousOrder = ordersList[currentOrderIndex - 1];
      navigate(`/app/orders/${previousOrder.id}`);
    }
  };

  // Navigation vers la commande suivante
  const goToNextOrder = () => {
    if (currentOrderIndex < ordersList.length - 1) {
      const nextOrder = ordersList[currentOrderIndex + 1];
      navigate(`/app/orders/${nextOrder.id}`);
    }
  };

  useEffect(() => {
    if (id) {
      loadOrderDetails();
      loadOrdersList();
    }
  }, [id]);

  // Couleur du statut de commande
  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'pending': return 'warning';
      case 'confirmed': return 'processing';
      case 'in_delivery': return 'cyan';
      case 'delivered': return 'success';
      case 'canceled': return 'error';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  // Traduction du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Nouveau';
      case 'pending': return 'En attente';
      case 'confirmed': return 'Confirmé';
      case 'in_delivery': return 'En livraison';
      case 'delivered': return 'Livré';
      case 'canceled': return 'Annulé';
      case 'completed': return 'Complété';
      default: return status;
    }
  };

  // Colonnes pour le tableau des articles
  const itemsColumns = [
    {
      title: 'Article',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Image du produit - Utilise les nouvelles relations */}
          {(record.product_variant?.product?.main_image || 
            record.product_variant?.image_url ||
            record.shopify_product?.main_image) && (
            <Image
              width={60}
              height={60}
              src={
                record.product_variant?.product?.main_image || 
                record.product_variant?.image_url ||
                record.shopify_product?.main_image
              }
              alt="Produit"
              style={{ 
                objectFit: 'cover', 
                marginRight: '12px', 
                borderRadius: '6px',
                border: '1px solid #f0f0f0'
              }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
            />
          )}
          <div>
            <div style={{ fontWeight: 500 }}>
              {/* Priorité: product_name de l'item, puis nom du produit, puis titre du variant */}
              {text || 
               record.product_variant?.product?.name || 
               record.product_variant?.title ||
               'Article inconnu'}
            </div>
            {/* Afficher le SKU et autres informations du variant */}
            {record.product_variant?.sku && (
              <Text code style={{ fontSize: '12px' }}>{record.product_variant.sku}</Text>
            )}
            {/* Afficher le type de produit si disponible */}
            {record.product_variant?.product?.product_type && (
              <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                {record.product_variant.product.product_type}
              </Text>
            )}
            {/* Afficher le vendor si disponible */}
            {record.product_variant?.product?.vendor && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                - {record.product_variant.product.vendor}
              </Text>
            )}
            {/* Notes spécifiques à l'article */}
            {record.notes && (
              <Text italic style={{ display: 'block', color: '#888', fontSize: '12px' }}>
                Note: {record.notes}
              </Text>
            )}
            {/* Statut de l'article si différent de pending */}
            {record.status && record.status !== 'pending' && (
              <Tag size="small" color={
                record.status === 'ready' ? 'green' : 
                record.status === 'in_kitchen' ? 'orange' : 
                record.status === 'served' ? 'blue' : 'default'
              }>
                {record.status}
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Prix unitaire',
      dataIndex: 'product_price',
      key: 'product_price',
      align: 'right',
      render: (price) => `${Number(price || 0).toFixed(2)} XOF`,
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      render: (qty) => <Tag color="blue">{qty}</Tag>,
    },
    {
      title: 'Total',
      key: 'total',
      align: 'right',
      render: (_, record) => {
        const total = (record.product_price || 0) * (record.quantity || 0);
        return <Text strong>{total.toFixed(2)} XOF</Text>;
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        <div style={{ marginTop: '16px' }}>
          <Text>Chargement des détails de la commande...</Text>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="danger">Commande non trouvée</Text>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête avec breadcrumb et bouton retour */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <Breadcrumb
            items={[
              {
                href: '/app/dashboard',
                title: <HomeOutlined />,
              },
              {
                href: '/app/orders',
                title: 'Commandes',
              },
              {
                title: `Commande #${order.order_number || order.id}`,
              },
            ]}
            className="mb-2"
          />
          <div className="flex items-center justify-between">
            <Title level={2}>
              Détails de la Commande #{order.order_number || order.id}
            </Title>
            
            {/* Navigation entre commandes */}
            {ordersList.length > 0 && (
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Button
                  icon={<LeftOutlined />}
                  size="small"
                  disabled={currentOrderIndex <= 0 || loadingNavigation}
                  onClick={goToPreviousOrder}
                  title="Commande précédente"
                />
                <span>
                  {currentOrderIndex + 1} / {ordersList.length}
                </span>
                <Button
                  icon={<RightOutlined />}
                  size="small"
                  disabled={currentOrderIndex >= ordersList.length - 1 || loadingNavigation}
                  onClick={goToNextOrder}
                  title="Commande suivante"
                />
              </div>
            )}
          </div>
        </div>
        <Space>
          <Button 
            icon={<EditOutlined />} 
            type="primary"
            onClick={handleEditClick}
            size="large"
          >
            Modifier
          </Button>
          {order.status === 'new' && (
            <Button 
              icon={<CarOutlined />} 
              type="default"
              onClick={showAssignmentModal}
              size="large"
            >
              Attribuer livreur
            </Button>
          )}
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/app/orders')}
            size="large"
          >
            Retour à la liste
          </Button>
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        {/* Informations générales */}
        <Col xs={24} lg={12}>
          <Card title="Informations générales" className="h-full">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Numéro de commande">
                <Text strong>{order.order_number || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <Tag color={getOrderStatusColor(order.status)}>
                  {getStatusLabel(order.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                <Tag color={order.source === 'shopify' ? 'green' : 'blue'}>
                  {order.source || 'Manuel'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Montant total">
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  {Number(order.total_amount || 0).toFixed(2)} XOF
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Date de création">
                {moment(order.created_at).format('DD/MM/YYYY à HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Dernière modification">
                {moment(order.updated_at).format('DD/MM/YYYY à HH:mm')}
              </Descriptions.Item>
              {order.delivery_date && (
                <Descriptions.Item label="Date de livraison prévue">
                  {moment(order.delivery_date).format('DD/MM/YYYY')}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        {/* Informations client et livraison */}
        <Col xs={24} lg={12}>
          <Card title="Client et Livraison" className="h-full">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Nom du client">
                <Text strong>{order.customer?.name || 'Client inconnu'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">
                {order.customer?.phone || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {order.customer?.email || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Livreur assigné">
                {order.delivery_person ? (
                  <div>
                    <Text strong>{order.delivery_person.name}</Text>
                    {order.delivery_person.phone && (
                      <div style={{ fontSize: '0.8em', color: '#888' }}>
                        {order.delivery_person.phone}
                      </div>
                    )}
                  </div>
                ) : (
                  <Text type="secondary" italic>Non assigné</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Adresse de livraison">
                <Text style={{ whiteSpace: 'pre-wrap' }}>
                  {order.delivery_address || 'Non fournie'}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Notes de la commande */}
      {order.delivery_notes && (
        <Card title="Notes de livraison" style={{ marginTop: '24px' }}>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{order.delivery_notes}</Text>
        </Card>
      )}

      {/* Articles de la commande */}
      <Card title="Articles commandés" style={{ marginTop: '24px' }}>
        <Table
          columns={itemsColumns}
          dataSource={order.order_items || []}
          rowKey="id"
          pagination={false}
          summary={(pageData) => {
            const total = pageData.reduce(
              (sum, item) => sum + (item.product_price || 0) * (item.quantity || 0),
              0
            );
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                    {total.toFixed(2)} XOF
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
        
        {(!order.order_items || order.order_items.length === 0) && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">Aucun article trouvé pour cette commande</Text>
          </div>
        )}
      </Card>

      {/* Modal de modification */}
      <Modal
        title="Modifier la commande"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="status"
            label="Statut de la commande"
            rules={[{ required: true, message: 'Veuillez sélectionner un statut' }]}
          >
            <Select placeholder="Sélectionner un statut">
              <Select.Option value="new">Nouveau</Select.Option>
              <Select.Option value="confirmed">Confirmé</Select.Option>
              <Select.Option value="preparing">En préparation</Select.Option>
              <Select.Option value="ready_to_deliver">Prêt à livrer</Select.Option>
              <Select.Option value="in_delivery">En livraison</Select.Option>
              <Select.Option value="delivered">Livré</Select.Option>
              <Select.Option value="completed">Complété</Select.Option>
              <Select.Option value="cancelled">Annulé</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="delivery_person_id"
            label="Livreur assigné"
          >
            <Select 
              placeholder="Sélectionner un livreur (optionnel)" 
              allowClear
              loading={loadingDeliveryPersons}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {deliveryPersons.map(person => (
                <Select.Option key={person.id} value={person.id}>
                  {person.name} {person.phone && `(${person.phone})`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="delivery_notes"
            label="Notes de livraison"
          >
            <Input.TextArea
              rows={3}
              placeholder="Notes particulières pour la livraison..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => setIsEditModalVisible(false)}
                icon={<CloseOutlined />}
              >
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={saving}
                icon={<SaveOutlined />}
              >
                Sauvegarder
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal d'attribution de livreur */}
      <Modal
        title="Attribuer un livreur à la commande"
        open={isAssignmentModalVisible}
        onCancel={() => setIsAssignmentModalVisible(false)}
        onOk={handleAssignOrder}
        okText="Attribuer"
        cancelText="Annuler"
        confirmLoading={saving}
        width={600}
      >
        <Form
          form={assignmentForm}
          layout="vertical"
        >
          <Form.Item
            label="Livreur"
            name="delivery_person_id"
            rules={[{ required: true, message: 'Veuillez sélectionner un livreur' }]}
          >
            <Select
              placeholder="Sélectionner un livreur"
              loading={loadingDeliveryPersons}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {deliveryPersons.map(person => {
                const stats = deliveryPersonsStats[person.id] || { orders_count: 0 };
                return (
                  <Select.Option key={person.id} value={person.id}>
                    <Space>
                      <UserOutlined />
                      <span>{person.name}</span>
                      {person.phone && <span style={{ color: '#666' }}>({person.phone})</span>}
                      <Tag color={stats.orders_count > 10 ? 'orange' : 'green'}>
                        {stats.orders_count} colis aujourd'hui
                      </Tag>
                    </Space>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            label="Date de livraison"
            name="delivery_date"
            tooltip="Laissez vide pour utiliser la date par défaut"
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD/MM/YYYY"
              placeholder="Sélectionner une date (optionnel)"
              disabledDate={(current) => {
                // Désactiver les dates avant aujourd'hui
                const today = moment().startOf('day');
                return current && current < today;
              }}
            />
          </Form.Item>

          <Form.Item
            label="Frais de livraison"
            name="shipping_fee"
            tooltip="Frais de livraison à appliquer"
          >
            <Input 
              type="number" 
              min={0} 
              step={500}
              addonAfter="XOF"
              placeholder="Frais de livraison"
            />
          </Form.Item>

          <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f6f6f6', borderRadius: '6px' }}>
            <Text strong>Commande à attribuer :</Text>
            <div style={{ marginTop: '8px' }}>
              <Text>N° {order?.order_number || order?.id}</Text><br />
              <Text>Client : {order?.customer?.name || 'N/A'}</Text><br />
              <Text>Montant : {Number(order?.total_amount || 0).toFixed(2)} XOF</Text>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderDetails; 